import type { ViewQueryNode } from '@salescore/core'
import { Array, HashSet, pipe } from 'effect'

import type { NewRecordChange } from '../../rsheet/action/addNewRecord/addNewRecords'
import type { DeepReadonly } from '../../rsheet/types/DeepReadonly'
import type { RecordChange } from '../../rsheet/util/updateViewRecords'
import { keyFunction } from '../generateChangesFromCopy/generateParentToChildRelationChanges'
import type { MagnifyingGlassChange } from './applyMagnifyingGlassChange'

/**
 * シートのセルへの変更に、編集権限のないオブジェクトへの書き込みが含まれるかを返す
 * - 編集権限は、変更を書き込むオブジェクトごとに判定する
 * @param canEditModelRecord 指定したオブジェクトのレコードを編集できるかを返す
 * @param canBeCreatedWithoutAbility ノードがシートのカスタム列かを返す
 */
export const hasChangesWithoutEditPermission = ({
  canEditModelRecord,
  newRecordChanges,
  recordChanges,
  magnifyingGlassChanges,
  canBeCreatedWithoutAbility,
}: {
  canEditModelRecord: (modelName: string | undefined) => boolean
  newRecordChanges: DeepReadonly<NewRecordChange[]>
  recordChanges: DeepReadonly<RecordChange[]>
  magnifyingGlassChanges: MagnifyingGlassChange[]
  canBeCreatedWithoutAbility: (node: DeepReadonly<ViewQueryNode>) => boolean
}): boolean => {
  // シートのカスタム列は、値を入れると内部でレコードを新規作成するが、ユーザーからは編集に見えるため、作成も編集権限で判定する
  const hasDeniedSheetCustomModelCreation = newRecordChanges.some(
    (newRecordChange) =>
      canBeCreatedWithoutAbility(newRecordChange.node) && !canEditModelRecord(newRecordChange.node.write?.streamName),
  )
  const creatingRecordKeys = pipe(
    newRecordChanges,
    Array.map((newRecordChange) =>
      keyFunction({
        nodePath: newRecordChange.node.path,
        rowIndex: newRecordChange.rowIndex,
        innerRowIndex: newRecordChange.innerRowIndex,
      }),
    ),
    HashSet.fromIterable,
  )
  const hasDeniedRecordChange = recordChanges.some(
    (recordChange) =>
      // レコードの削除は削除権限で判定するため、編集権限では判定しない
      recordChange.isDelete !== true &&
      recordChange.isDeleteAllChildren !== true &&
      // 同じ操作で新規作成するレコードへの値 (行追加時の初期値・ペーストで増えた行の値) は、新規作成の一部として編集権限で判定しない
      !HashSet.has(creatingRecordKeys, keyFunction(recordChange)) &&
      !canEditModelRecord(recordChange.field.write?.streamName),
  )
  // 虫眼鏡セルでの参照先の選択は、参照元のレコードの参照項目を書き換えるため、参照元のオブジェクトの編集権限で判定する
  const hasDeniedMagnifyingGlassChange = magnifyingGlassChanges.some(
    (magnifyingGlassChange) => !canEditModelRecord(magnifyingGlassChange.parentNode.write?.streamName),
  )
  return hasDeniedSheetCustomModelCreation || hasDeniedRecordChange || hasDeniedMagnifyingGlassChange
}
